// Auth for admin app: use Supabase when env present, else fallback to mock.
import { getSupabaseClient } from './supabase';

const KEY = "gc_admin_token";

export type User = { email: string; role: "admin" | "guide" | "seeker" };

function getEnv(): any {
    return (window as any).__ENV__;
}

function hasSupabaseEnv(): boolean {
    const env = getEnv();
    return !!(env?.SUPABASE_URL && env?.SUPABASE_ANON_KEY);
}

function getSB() {
    return getSupabaseClient();
}

export function getUser(): User | null {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as User) : null;
}

export async function signIn(email: string, password: string): Promise<User> {
    // If Supabase env is available, use real auth
    if (hasSupabaseEnv()) {
        const sb = getSB();
        const { data, error } = await sb.auth.signInWithPassword({ email, password });
        if (error) throw new Error(error.message || "Login failed");
        const userId = data.user?.id;
        if (!userId) throw new Error("No user returned");

        // Check admin role via RLS-readable view/table
        const { data: adminRows, error: adminErr } = await sb
            .from("admins")
            .select("user_id")
            .eq("user_id", userId)
            .limit(1);
        if (adminErr) throw new Error(adminErr.message);
        const isAdmin = (adminRows?.length ?? 0) > 0;
        if (!isAdmin) {
            await sb.auth.signOut();
            throw new Error("This account is not an admin");
        }

        const user: User = { email, role: "admin" };
        localStorage.setItem(KEY, JSON.stringify(user));
        return user;
    }

    // Fallback mock: basic validation then treat as admin
    if (!email || password.length < 4) {
        throw new Error("Invalid credentials");
    }
    const user: User = { email, role: "admin" };
    localStorage.setItem(KEY, JSON.stringify(user));
    return user;
}

export async function signOut() {
    localStorage.removeItem(KEY);
    if (hasSupabaseEnv()) {
        try {
            const sb = getSB();
            await sb.auth.signOut();
        } catch { }
    }
}

export function isAuthed(): boolean {
    return getUser()?.role === 'admin';
}
